import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DELIVERY_NOTE_STATE, useDeliveryNotesStore } from "../../stores/delivery_notes";
import Search from "../form/Search";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "./Modal";

const currentState = (deliveryNote) =>
  deliveryNote.state_transitions?.slice(-1)[0]?.state || DELIVERY_NOTE_STATE.DRAFTED;

// Picker for linking an existing delivery note to a work order. The backend
// is authoritative for permissions; canceled notes and notes that already
// belong to a work order (1:n) are disabled up front.
function LinkDeliveryNoteDialog({ onSelect, onClose }) {
  const { t } = useTranslation();
  const searchSome = useDeliveryNotesStore((state) => state.searchSome);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    let active = true;
    searchSome({ q: search, limit: 10 }).then(({ data }) => {
      if (active) {
        setResults(data || []);
      }
    });
    return () => {
      active = false;
    };
  }, [search, searchSome]);

  return (
    <Modal size="lg">
      <ModalHeader>
        <h5 className="modal-title">{t("work_orders.link_form")}</h5>
      </ModalHeader>
      <ModalBody>
        <Search search={search} handleSearch={(e) => setSearch(e.target.value)} />
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th className="text-nowrap" style={{ width: "1%" }}>
                  {t("delivery_notes.delivery_note")}
                </th>
                <th>{t("customers.customer")}</th>
                <th>{t("customers.properties.property")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.map((deliveryNote) => {
                const state = currentState(deliveryNote);
                const isCanceled = state === DELIVERY_NOTE_STATE.CANCELED;
                const isLinked = deliveryNote.work_order != null;
                const rowClass = isCanceled || isLinked ? "text-muted" : "";
                const disabledTitle = isCanceled
                  ? t("work_orders.errors.delivery_note_canceled")
                  : isLinked
                    ? t("work_orders.errors.already_linked")
                    : undefined;
                // Disabled buttons swallow hover events (no native tooltip in
                // Firefox); pe-none routes hover to the title-bearing wrapper.
                const linkButtonClass = disabledTitle
                  ? "btn btn-sm btn-success pe-none"
                  : "btn btn-sm btn-success";
                const customer = deliveryNote.customer;
                const customerProperty = deliveryNote.customer_property;
                const propertyName = customerProperty?.name?.trim();
                const customerName = customer?.name?.trim();
                const showCustomerName = !!customerName && customerName !== propertyName;
                return (
                  <tr key={deliveryNote.id} className={rowClass}>
                    <td className="text-nowrap">
                      <Link to={`/delivery_notes/${deliveryNote.id}/details`} className="fw-bold">
                        {deliveryNote.number}
                      </Link>
                      <div className="mt-1">
                        <span className="badge bg-default text-dark">
                          {t(`delivery_notes.states.${state}`)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <ul className="list-group list-unstyled">
                        {!!customer?.number && <li>{customer.number}</li>}
                        {showCustomerName && (
                          <li className="text-truncate" style={{ maxWidth: "25ch" }} title={customerName}>
                            {customerName}
                          </li>
                        )}
                      </ul>
                    </td>
                    <td>
                      <ul className="list-group list-unstyled">
                        {!!propertyName && (
                          <li className="text-truncate" style={{ maxWidth: "25ch" }} title={propertyName}>
                            {propertyName}
                          </li>
                        )}
                        {customerProperty?.addresses?.map((address) => {
                          const nameMatchesStreet = propertyName === address.street?.trim();
                          return (
                            <li key={address.id}>
                              <ul className="list-group list-unstyled">
                                {!!address.street && !nameMatchesStreet && <li>{address.street}</li>}
                                {(!!address.zip || !!address.city) && (
                                  <li>
                                    {address.zip} {address.city}
                                  </li>
                                )}
                              </ul>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                    <td className="text-end">
                      <span className="d-inline-block" title={disabledTitle}>
                        <button
                          type="button"
                          className={linkButtonClass}
                          disabled={isCanceled || isLinked}
                          onClick={() => onSelect(deliveryNote.id)}
                        >
                          {t("work_orders.link")}
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-default" onClick={onClose}>
          {t("menu.cancel")}
        </button>
      </ModalFooter>
    </Modal>
  );
}

LinkDeliveryNoteDialog.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LinkDeliveryNoteDialog;
